/**
 * Step-by-step guides for the /programar-<red> landings.
 *
 * Search Console shows these pages getting impressions for how-to queries
 * ("cómo programar post en linkedin", "programar un tiktok", "programar hilo
 * twitter"…) while ranking 15–50, because the page only sold the product and
 * never answered the question. Google ranks tutorials for these queries, so
 * the landing now carries one: the network's own method where it exists, and
 * the Zettapost one.
 *
 * The native steps are only written for networks whose built-in scheduler is
 * stable and well known. For Threads, Pinterest and Bluesky the guide sticks
 * to Zettapost rather than describe menus that change often.
 */
import type { LandingFaq } from '../components/LandingTemplate';

export type LandingGuide = {
  /** Network name as it reads in a sentence ("LinkedIn", "X"). */
  network: string;
  /** What gets uploaded, for the Zettapost steps ("la imagen, el vídeo o el PDF"). */
  media: string;
  intro: string;
  /** How the account is connected, when it isn't the network's own login. */
  connect?: string;
  native?: { steps: string[]; note: string };
  tips: string[];
  faqs: LandingFaq[];
};

/** The Zettapost flow, the same for every network. */
export function zettapostSteps(g: LandingGuide): string[] {
  return [
    `Crea tu cuenta (3 días gratis) y conecta ${g.network} desde «Cuentas». ${g.connect ?? `Se conecta con el inicio de sesión oficial de ${g.network}: no nos das tu contraseña.`}`,
    `Pulsa «Nueva publicación», marca tu cuenta de ${g.network}, escribe el texto (o pide un borrador a la IA) y sube ${g.media}.`,
    `Revisa la vista previa: ves cómo quedará en ${g.network} antes de programarlo.`,
    'Elige día y hora, o añádelo a la cola para que salga en tu próximo hueco libre.',
    `Pulsa «Programar». Zettapost lo publica a su hora y, si ${g.network} lo rechaza, te avisa con el motivo para que lo corrijas.`,
  ];
}

const freeFaq = (network: string, native: boolean): LandingFaq => ({
  q: `¿Se puede programar en ${network} gratis?`,
  a: native
    ? `Sí, desde la propia ${network}, con sus limitaciones: una cuenta cada vez y sin calendario común con tus otras redes. Con Zettapost tienes 3 días de prueba gratis para programar en ${network} y en el resto de redes desde un solo calendario; después eliges plan, sin permanencia.`
    : `Con Zettapost tienes 3 días de prueba gratis para programar en ${network} y en el resto de redes desde un solo calendario. Después eliges plan, sin permanencia.`,
});

export const LANDING_GUIDES = {
  linkedin: {
    network: 'LinkedIn',
    media: 'la imagen, el vídeo o el PDF',
    intro:
      'Puedes programar publicaciones en LinkedIn desde la propia red o con Zettapost. LinkedIn sirve si llevas una sola cuenta; si gestionas tu perfil y una o varias páginas, o también otras redes, compensa hacerlo desde un calendario único.',
    native: {
      steps: [
        'En LinkedIn (web o app), pulsa «Crear publicación» en tu perfil, o desde la vista de administrador de tu página de empresa.',
        'Escribe el texto y añade la imagen, el vídeo o el documento.',
        'Pulsa el icono del reloj, junto a «Publicar».',
        'Elige fecha y hora y confirma con «Programar».',
      ],
      note: 'Para ver o cambiar lo programado, abre de nuevo «Crear publicación», pulsa el reloj y entra en «Ver todas las publicaciones programadas». Si llevas varias páginas, tienes que hacerlo página a página.',
    },
    tips: [
      'Programa lo estable (consejos, casos, anuncios) y publica en directo lo que depende de la actualidad: una reacción a una noticia que sale con horas de retraso se nota.',
      'Cuida las dos primeras líneas: es lo único que se ve antes de «ver más».',
      'Los documentos en PDF se muestran como carrusel y funcionan bien para guías y listas.',
      'Deja tiempo para responder a los comentarios de la primera hora; programar no es desaparecer.',
    ],
    faqs: [
      freeFaq('LinkedIn', true),
      {
        q: '¿Con cuánta antelación se puede programar?',
        a: 'Desde LinkedIn, con unos meses de margen. Con Zettapost no hay un límite práctico: puedes dejar listo el calendario del trimestre y moverlo cuando cambien los planes.',
      },
    ],
  },
  x: {
    network: 'X',
    media: 'las imágenes o el vídeo',
    intro:
      'Puedes programar posts en X desde la versión web de X o con Zettapost. X sirve para un post suelto en una cuenta; para varias cuentas o un calendario junto a tus otras redes, compensa Zettapost.',
    native: {
      steps: [
        'En x.com, abre el cuadro para escribir un post.',
        'Escribe el texto y añade imágenes o vídeo.',
        'Pulsa el icono del calendario («Programar»), elige fecha y hora y confirma.',
        'Pulsa «Programar».',
      ],
      note: 'Lo programado queda en los posts sin enviar, en la pestaña de programados. Se hace desde la web, cuenta a cuenta.',
    },
    tips: [
      'Repasa lo programado los días con una noticia o conversación sensible: en X, un post fuera de contexto se nota enseguida.',
      'Si tienes X Premium, aprovecha los posts largos (hasta 2.200 caracteres al programar) para lo que antes partías en un hilo.',
      'Deja huecos para conversar: en X lo que funciona es responder, y eso no se programa.',
    ],
    faqs: [
      freeFaq('X', true),
      {
        q: '¿Se pueden programar hilos en X?',
        a: 'Zettapost todavía no publica hilos: programa posts sueltos, con imágenes o vídeo. Un hilo, de momento, hay que publicarlo desde X.',
      },
    ],
  },
  facebook: {
    network: 'Facebook',
    media: 'las fotos o el vídeo',
    intro:
      'Las publicaciones de una página de Facebook se pueden programar desde Meta Business Suite o con Zettapost. Business Suite cubre Facebook e Instagram; si también publicas en LinkedIn, X, TikTok u otras redes, Zettapost lo junta todo en un calendario.',
    native: {
      steps: [
        'Entra en Meta Business Suite y selecciona tu página.',
        'Pulsa «Crear publicación» y añade el texto y las fotos o el vídeo.',
        'En las opciones de publicación, elige «Programar» y fija fecha y hora.',
        'Confirma con «Programar».',
      ],
      note: 'Funciona con páginas, no con perfiles personales. Lo programado aparece en el «Planificador» de Business Suite.',
    },
    tips: [
      'Prepara una versión propia para Facebook en lugar de copiar la de Instagram: los enlaces funcionan y el texto puede ser más largo.',
      'Revisa la vista previa del enlace antes de programar; si la imagen no carga, el post pierde mucho.',
    ],
    faqs: [freeFaq('Facebook', true)],
  },
  instagram: {
    network: 'Instagram',
    media: 'las fotos, el carrusel o el reel',
    connect: 'Se conecta con el inicio de sesión oficial de Instagram o de Facebook (para cuentas de empresa): no nos das tu contraseña.',
    intro:
      'Con una cuenta profesional, Instagram deja programar desde su app y desde Meta Business Suite. Zettapost añade lo que falta: programar desde el ordenador junto a tus otras redes, con vista previa y aviso si algo falla.',
    native: {
      steps: [
        'Cambia a cuenta profesional (empresa o creador) si aún no lo es.',
        'En la app, crea la publicación o el reel y avanza hasta la última pantalla.',
        'Antes de compartir, busca la opción «Programar» en las opciones avanzadas y elige fecha y hora.',
        'Vuelve atrás y pulsa «Programar».',
      ],
      note: 'También puedes hacerlo desde Meta Business Suite en el ordenador. Con una cuenta personal no aparece la opción de programar.',
    },
    tips: [
      'Prepara la portada del reel: es lo que se ve en tu perfil.',
      'En carruseles, la primera imagen hace de gancho; deja el detalle para las siguientes.',
    ],
    faqs: [freeFaq('Instagram', true)],
  },
  tiktok: {
    network: 'TikTok',
    media: 'el vídeo',
    intro:
      'TikTok permite programar vídeos desde el ordenador, en TikTok Studio, con pocos días de antelación. Con Zettapost los programas cuando quieras, desde el ordenador y junto al resto de tus redes.',
    native: {
      steps: [
        'Desde el ordenador, entra en TikTok Studio y sube el vídeo.',
        'Añade la descripción, la portada y los ajustes de privacidad.',
        'En «Cuándo publicar», elige «Programar» y fija fecha y hora.',
        'Pulsa «Programar».',
      ],
      note: 'Se programa vídeo a vídeo y con un margen corto de días, así que no da para planificar el mes.',
    },
    tips: [
      'Los primeros segundos deciden si se ve el resto: empieza por lo más llamativo.',
      'Escribe la descripción pensando en la búsqueda dentro de TikTok: qué escribiría alguien para encontrar ese vídeo.',
    ],
    faqs: [freeFaq('TikTok', true)],
  },
  youtube: {
    network: 'YouTube',
    media: 'el vídeo o el Short',
    intro:
      'YouTube deja programar vídeos y Shorts desde YouTube Studio. Zettapost lo hace desde el mismo calendario que el resto de tus redes, para que el estreno en YouTube y su difusión en las demás salgan coordinados.',
    native: {
      steps: [
        'En YouTube Studio, pulsa «Crear» y «Subir vídeos».',
        'Rellena el título, la descripción y la miniatura.',
        'En «Visibilidad», elige «Programar» y fija fecha, hora y zona horaria.',
        'Pulsa «Programar».',
      ],
      note: 'Los Shorts se programan igual si los subes desde YouTube Studio en el ordenador.',
    },
    tips: [
      'Programa a la vez los posts que anuncian el vídeo en tus otras redes, para que salgan cuando ya está publicado.',
      'Revisa la miniatura en tamaño pequeño: es como la verá casi todo el mundo.',
    ],
    faqs: [freeFaq('YouTube', true)],
  },
  threads: {
    network: 'Threads',
    media: 'las imágenes o el vídeo',
    intro:
      'Con Zettapost programas tus publicaciones de Threads desde el ordenador, en el mismo calendario que Instagram y el resto de tus redes.',
    tips: [
      'Threads premia la conversación: publica preguntas y opiniones, no solo anuncios.',
      'Si vienes de X, adapta el tono en lugar de copiar el mismo texto en las dos redes.',
    ],
    faqs: [freeFaq('Threads', false)],
  },
  pinterest: {
    network: 'Pinterest',
    media: 'la imagen o el vídeo vertical',
    intro:
      'Con Zettapost programas tus pines desde el ordenador, con su tablero y su enlace, en el mismo calendario que el resto de tus redes.',
    tips: [
      'Usa imágenes verticales: ocupan más espacio en el feed.',
      'Escribe título y descripción pensando en la búsqueda: Pinterest funciona como un buscador.',
      'Reparte los pines a lo largo de la semana en lugar de publicarlos todos de golpe.',
    ],
    faqs: [freeFaq('Pinterest', false)],
  },
  bluesky: {
    network: 'Bluesky',
    media: 'las imágenes',
    connect:
      'Se conecta con tu usuario y una contraseña de aplicación, que creas en los ajustes de Bluesky: no es tu contraseña normal y puedes revocarla cuando quieras.',
    intro:
      'Con Zettapost programas tus publicaciones de Bluesky desde el ordenador, en el mismo calendario que X, Threads y el resto de tus redes.',
    tips: [
      'Bluesky tiene su propio público: adapta el tono en lugar de duplicar lo que publicas en X.',
      'Ten en cuenta el límite de 300 caracteres por post: si el texto viene de LinkedIn o Facebook, recórtalo.',
    ],
    faqs: [freeFaq('Bluesky', false)],
  },
} satisfies Record<string, LandingGuide>;
